import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { authAPI, getErrorMessage } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Shield, Eye, EyeOff, ArrowRight, Quote, Star } from 'lucide-react';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totp_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      const { access_token, user, email_verified } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      // Check if email is verified
      if (!email_verified) {
        localStorage.setItem('pendingEmailVerification', user.email);
        toast.info('Please verify your email to access all features');
        navigate('/auth/email-verification');
        return;
      }

      toast.success('Login successful!');

      if (user.role === 'user') {
        navigate('/user');
      } else {
        navigate('/admin');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Login failed');
      if (errorMessage === '2FA code required' || error.response?.data?.detail === '2FA code required') {
        setRequires2FA(true);
        toast.info('Please enter your 2FA code');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center h-full bg-[#060638]">
      <div className="w-full overflow-hidden flex flex-col md:flex-row min-h-[80vh] md:h-[90vh] my-auto gap-6 md:gap-10 bg-transparent px-4 md:px-6 pt-4 md:pt-0">
        {/* Left Panel - Image */}
        <div className="w-full md:w-1/2 flex items-center justify-center relative overflow-hidden rounded-2xl min-h-[240px] sm:min-h-[320px] md:min-h-0 md:h-full shrink-0 p-4 md:p-0">
          <img
            src="/login.png"
            alt=""
            className="w-full h-auto object-contain rounded-2xl md:h-full md:w-full"
          />
        </div>

        {/* Right Panel - Form */}
        <div className="w-full md:w-1/2 p-4 md:p-8 lg:p-10 xl:p-12 flex flex-col justify-start">
          <div className="flex items-center justify-between w-full max-w-[630px] mb-6 md:mb-10 lg:mb-12">
            <img src="/images/logo.png" alt="Logo" className="h-8 md:h-10 lg:h-12 mr-auto" />
            <Link
              to="/register"
              className="text-white hover:underline items-center font-medium border border-white rounded-full px-3 md:px-4 py-1.5 md:py-2 justify-center text-sm md:text-base"
              data-testid="register-link"
            >
              Sign Up
            </Link>
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-[44px] font-bold text-white mb-6 md:mb-8 lg:mb-10">
            <span className="!text-white">Login</span>
          </h1>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5 w-full max-w-[630px]"
            data-testid="login-form"
          >
            <div className="w-full">
              {/* <Label
                htmlFor="email"
                className="text-sm font-medium text-white mb-2 block"
              >
                Email <span className="text-red-500">*</span>
              </Label> */}
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full h-12 !bg-white border-0 text-[16px] rounded-none px-4 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              {/* <Label
                htmlFor="password"
                className="text-sm font-medium text-white mb-2 block"
              >
                Password <span className="text-red-500">*</span>
              </Label> */}
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full h-12 !bg-white border-0 rounded-none px-4 pr-12 !text-gray-900 text-[16px] placeholder:text-[16px] placeholder:text-gray-400 focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

            </div>

            {requires2FA && (
              <div>
                <Label
                  htmlFor="totp_code"
                  className="text-sm font-medium text-white mb-2 block"
                >
                  2FA Code <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="totp_code"
                    type="text"
                    placeholder="123456"
                    value={formData.totp_code}
                    onChange={(e) =>
                      setFormData({ ...formData, totp_code: e.target.value })
                    }
                    className="w-full h-12 !bg-white border-0 rounded-lg pl-12 pr-4 !text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                    maxLength={6}
                    data-testid="2fa-input"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-14 bg-[#ff2d2d] hover:bg-[#7f1d1d] text-white font-semibold rounded-none shadow-xl flex items-center justify-center gap-2 transition-all duration-300 transition-ease-in-out mt-5"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  <span>Login</span>
                </>
              )}
            </Button>

            <div className="text-left mt-2">
              <Link
                to="/auth/forgot-password"
                className="text-md font-semibold text-white/80 hover:text-white"
              >
                I forgot my password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div >
  );
};

export default Login;
